import { SiteHeader } from "@/components/site-header"
import { CodeBlock } from "@/components/code-block"

export default function CrudEntitiesGuidePage() {
  return (
    <>
      <SiteHeader title="Guía CRUD de Entidades" />
      <div className="flex flex-1 flex-col px-4 py-6 lg:px-6">
        <div className="max-w-5xl space-y-10">

          <section>
            <h2 className="text-base font-semibold mb-2">Objetivo</h2>
            <p className="text-sm text-muted-foreground">
              Guía paso a paso para implementar una nueva entidad con borrado lógico (soft delete), restauración,
              filtros de estado y tests, siguiendo exactamente los patrones del módulo de Geografía.
              El ejemplo de referencia es la entidad <span className="font-medium">Ciudades</span>.
            </p>
          </section>

          {/* ─── 1. Schema DB ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">1 · Schema DB — <code className="text-xs font-mono">apps/api/src/db/schema.ts</code></h2>
            <p className="text-sm text-muted-foreground mb-3">
              Toda entidad que soporte soft delete necesita <code className="text-xs font-mono">deletedAt</code>. Los campos de código deben ser <code className="text-xs font-mono">NOT NULL</code>.
            </p>
            <CodeBlock code={`export const warehouses = pgTable('warehouses', {
  id:        uuid('id').primaryKey().$defaultFn(() => uuidv7()),
  code:      varchar('code', { length: 10 }).notNull(),          // obligatorio, max 10
  name:      varchar('name', { length: 150 }).notNull(),
  countryId: uuid('country_id').notNull().references(() => countries.id, { onDelete: 'cascade' }),
  deletedAt: timestamp('deleted_at', { withTimezone: true }),     // soft delete
  createdAt: timestamp('created_at', { withTimezone: true }).defaultNow().notNull(),
  updatedAt: timestamp('updated_at', { withTimezone: true }).defaultNow().notNull(),
}, (t) => [
  unique('uq_warehouses_code').on(t.code),
])

export type Warehouse = typeof warehouses.$inferSelect`} />
            <p className="text-sm text-muted-foreground mt-3">Luego: <code className="text-xs font-mono">cd apps/api && bun run db:push</code> (dev) o <code className="text-xs font-mono">db:generate && db:migrate</code> (prod).</p>
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
              ⚠️ En el archivo <code className="text-xs font-mono">.sql</code> usar <code className="text-xs font-mono">id uuid PRIMARY KEY NOT NULL</code> — sin DEFAULT. Drizzle aplica <code className="text-xs font-mono">$defaultFn(() =&gt; uuidv7())</code> en la app antes del INSERT. <code className="text-xs font-mono">DEFAULT uuid_generate_v7()</code> no existe en PostgreSQL estándar y rompe <code className="text-xs font-mono">db:migrate</code>.
            </p>
          </section>

          {/* ─── 2. Zod schemas ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">2 · Validaciones — <code className="text-xs font-mono">packages/shared/src/schemas/warehouses.ts</code></h2>
            <p className="text-sm text-muted-foreground mb-3">
              Los campos obligatorios deben tener <code className="text-xs font-mono">.min(1)</code>. El <code className="text-xs font-mono">updateSchema</code> es siempre <code className="text-xs font-mono">.partial()</code>.
            </p>
            <p className="text-sm text-amber-600 bg-amber-50 border border-amber-200 rounded px-3 py-2 mt-2">
              ⚠️ Nunca definir <code className="text-xs font-mono">updateSchema</code> manualmente con campos <code className="text-xs font-mono">.optional()</code>. Usar siempre <code className="text-xs font-mono">createSchema.partial()</code> — si se agrega un campo al createSchema, el updateSchema lo hereda automáticamente.
            </p>
            <CodeBlock code={`import { z } from "zod"

export const createWarehouseSchema = z.object({
  code:      z.string().min(1, "El código es requerido").max(10).toUpperCase(),
  name:      z.string().min(1, "El nombre es requerido").max(150),
  countryId: z.string().uuid("ID de país inválido"),
})

export const updateWarehouseSchema = createWarehouseSchema.partial()

export type CreateWarehouseInput = z.infer<typeof createWarehouseSchema>
export type UpdateWarehouseInput = z.infer<typeof updateWarehouseSchema>`} />
            <p className="text-sm text-muted-foreground mt-3">
              Exportar en <code className="text-xs font-mono">packages/shared/src/index.ts</code>:
            </p>
            <CodeBlock code={`export * from "./schemas/warehouses"`} />
          </section>

          {/* ─── 3. API route ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">3 · API route — <code className="text-xs font-mono">apps/api/src/routes/warehouses.ts</code></h2>
            <p className="text-sm text-muted-foreground mb-3">
              Importante: las rutas DEBEN estar encadenadas desde <code className="text-xs font-mono">new Hono()</code>.
              Si se usan <code className="text-xs font-mono">route.get()</code> separados, el cliente frontend pierde los tipos.
            </p>
            <CodeBlock code={`import { Hono } from "hono"
import { zValidator } from "@hono/zod-validator"
import { eq, isNull, isNotNull, and } from "drizzle-orm"
import { db } from "../db/client"
import { warehouses, countries } from "../db/schema"
import { requireAuth, requireAdmin } from "../middleware/context"
import { createWarehouseSchema, updateWarehouseSchema } from "@cdc-sccr/shared"
import { logger } from "../lib/logger"
import { uuidv7 } from "uuidv7"

const route = new Hono()

  // GET con filtro ?deleted=true|false (sin parámetro = todos)
  .get("/", requireAuth, async (c) => {
    const deleted = c.req.query("deleted")
    const where = []
    if (deleted === "true")       where.push(isNotNull(warehouses.deletedAt))
    else if (deleted === "false") where.push(isNull(warehouses.deletedAt))

    const result = await db
      .select({
        id:          warehouses.id,
        code:        warehouses.code,
        name:        warehouses.name,
        deletedAt:   warehouses.deletedAt,
        countryId:   warehouses.countryId,
        countryName: countries.name,
      })
      .from(warehouses)
      .innerJoin(countries, eq(warehouses.countryId, countries.id))
      .where(where.length > 0 ? and(...where) : undefined)
      .orderBy(warehouses.name)
    return c.json(result)
  })

  .post("/", requireAuth, requireAdmin,
    zValidator("json", createWarehouseSchema), async (c) => {
      const data  = c.req.valid("json")
      const actor = c.get("user")!
      const [created] = await db.insert(warehouses).values(data).returning()
      logger.info({ by: actor.sub, id: created?.id }, "Warehouse created")
      return c.json(created, 201)
    })

  .put("/:id", requireAuth, requireAdmin,
    zValidator("json", updateWarehouseSchema), async (c) => {
      const id   = c.req.param("id")
      const data = c.req.valid("json")
      const actor = c.get("user")!
      const [target] = await db.select({ id: warehouses.id, deletedAt: warehouses.deletedAt }).from(warehouses)
        .where(eq(warehouses.id, id)).limit(1)
      if (!target) return c.json({ error: "Depósito no encontrado" }, 404)
      if (target.deletedAt) return c.json({ error: "No se puede editar un depósito eliminado" }, 409)
      const [updated] = await db.update(warehouses)
        .set({ ...data, updatedAt: new Date() }).where(eq(warehouses.id, id)).returning()
      logger.info({ by: actor.sub, id }, "Warehouse updated")
      return c.json(updated)
    })

  // Soft delete: setea deletedAt en lugar de borrar el registro
  .delete("/:id", requireAuth, requireAdmin, async (c) => {
    const id    = c.req.param("id")
    const actor = c.get("user")!
    const [target] = await db.select({ id: warehouses.id, deletedAt: warehouses.deletedAt })
      .from(warehouses).where(eq(warehouses.id, id)).limit(1)
    if (!target) return c.json({ error: "Depósito no encontrado" }, 404)
    if (target.deletedAt) return c.json({ error: "Depósito ya eliminado" }, 409)
    await db.update(warehouses)
      .set({ deletedAt: new Date(), updatedAt: new Date() }).where(eq(warehouses.id, id))
    logger.info({ by: actor.sub, id }, "Warehouse soft deleted")
    return c.json({ ok: true })
  })

  // Restore: revierte deletedAt a null
  .patch("/:id/restore", requireAuth, requireAdmin, async (c) => {
    const id    = c.req.param("id")
    const actor = c.get("user")!
    const [target] = await db.select({ id: warehouses.id, deletedAt: warehouses.deletedAt })
      .from(warehouses).where(eq(warehouses.id, id)).limit(1)
    if (!target) return c.json({ error: "Depósito no encontrado" }, 404)
    if (!target.deletedAt) return c.json({ error: "Depósito ya activo" }, 409)
    await db.update(warehouses)
      .set({ deletedAt: null, updatedAt: new Date() }).where(eq(warehouses.id, id))
    logger.info({ by: actor.sub, id }, "Warehouse restored")
    return c.json({ ok: true })
  })

export default route`} />
            <p className="text-sm text-muted-foreground mt-3">
              Registrar en <code className="text-xs font-mono">apps/api/src/routes/index.ts</code>:
            </p>
            <CodeBlock code={`import warehousesRoute from "./warehouses"

const api = new Hono()
  .route("/auth", auth)
  .route("/users", usersRoute)
  .route("/geo", geo)
  .route("/warehouses", warehousesRoute)  // ← agregar`} />
          </section>

          {/* ─── 4. Frontend ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">4 · Frontend — <code className="text-xs font-mono">apps/web/src/pages/WarehousesPage.tsx</code></h2>
            <p className="text-sm text-muted-foreground mb-3">
              Patrón completo: filtros + paginación + soft delete + restore.
              El botón de restaurar reemplaza editar/eliminar cuando <code className="text-xs font-mono">row.deletedAt</code> tiene valor.
            </p>

            <h3 className="text-sm font-medium mb-2 mt-4">Tipos e interface</h3>
            <CodeBlock code={`interface WarehouseRow {
  id: string
  code: string
  name: string
  deletedAt: string | null  // ← siempre incluir para el botón de restore
  countryId: string
  countryName: string
}`} />

            <h3 className="text-sm font-medium mb-2 mt-4">Estado del filtro de borrados</h3>
            <CodeBlock code={`const [filterStatus, setFilterStatus] = useState<"active" | "deleted" | "all">("active")`} />

            <h3 className="text-sm font-medium mb-2 mt-4">Query con filterStatus</h3>
            <p className="text-sm text-muted-foreground mb-2">
              Usar <code className="text-xs font-mono">fetch</code> nativo cuando el query param no está tipado en el Hono client:
            </p>
            <CodeBlock code={`const API_URL = import.meta.env.VITE_API_URL ?? "http://localhost:3001"

const { data: rows = [], isLoading } = useQuery({
  queryKey: ["warehouses", "list", filterStatus],
  queryFn: async () => {
    const params = new URLSearchParams()
    if (filterStatus === "deleted") params.set("deleted", "true")
    else if (filterStatus === "active") params.set("deleted", "false")
    const baseUrl = API_URL + "/api/warehouses"
    const url = params.size ? baseUrl + "?" + params.toString() : baseUrl
    const r = await authorizedFetch(url)
    return r.json() as Promise<WarehouseRow[]>
  },
})`} />

            <h3 className="text-sm font-medium mb-2 mt-4">Mutation de restore</h3>
            <CodeBlock code={`const [restoringId, setRestoringId] = useState<string | null>(null)

const restoreMut = useMutation({
  mutationFn: async (id: string) => {
    setRestoringId(id)
    const url = API_URL + "/api/warehouses/" + id + "/restore"
    await authorizedFetch(url, { method: "PATCH" })
  },
  onSuccess: () => {
    qc.invalidateQueries({ queryKey: ["warehouses"] })
    setRestoringId(null)
  },
  onError: (err: Error) => {
    setFormError(err.message)
    setRestoringId(null)
  },
})`} />

            <h3 className="text-sm font-medium mb-2 mt-4">Filtro de estado en toolbar</h3>
            <CodeBlock code={`<Select value={filterStatus} onValueChange={(v) => { setFilterStatus(v as "active" | "deleted" | "all"); setPage(1) }}>
  <SelectTrigger className="w-40">
    <SelectValue />
  </SelectTrigger>
  <SelectContent>
    <SelectItem value="active">Activos</SelectItem>
    <SelectItem value="deleted">Borrados</SelectItem>
    <SelectItem value="all">Todos</SelectItem>
  </SelectContent>
</Select>`} />

            <h3 className="text-sm font-medium mb-2 mt-4">Botones de acción (restore vs editar/eliminar)</h3>
            <CodeBlock code={`{isAdmin && (
  <TableCell className="text-right pr-4">
    <div className="flex justify-end gap-1">
      {row.deletedAt ? (
        // Registro borrado → solo mostrar botón de restaurar
        <Button variant="ghost" size="icon-sm"
          onClick={() => restoreMut.mutate(row.id)}
          disabled={restoringId === row.id}
          className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
          title="Restaurar">
          <RestoreIcon className="size-3.5" />
        </Button>
      ) : (
        // Registro activo → editar y eliminar
        <>
          <Button variant="ghost" size="icon-sm" onClick={() => openEdit(row)} title="Editar">
            <PencilIcon className="size-3.5" />
          </Button>
          <Button variant="ghost" size="icon-sm"
            onClick={() => { setDeleting(row); setDeleteOpen(true) }}
            className="text-destructive hover:text-destructive hover:bg-destructive/10"
            title="Eliminar">
            <Trash2Icon className="size-3.5" />
          </Button>
        </>
      )}
    </div>
  </TableCell>
)}`} />
          </section>

          {/* ─── 5. Tests ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">5 · Tests</h2>
            <p className="text-sm text-muted-foreground mb-3">
              Dos niveles: funciones puras en shared, y schemas Zod. Los tests de API se escriben con una app Hono mínima sin conexión a DB.
            </p>

            <h3 className="text-sm font-medium mb-2">Schemas Zod</h3>
            <CodeBlock code={`import { describe, it, expect } from "bun:test"
import { createWarehouseSchema } from "@cdc-sccr/shared"

const UUID = "019e2c00-0000-7000-8000-000000000000"

describe("createWarehouseSchema", () => {
  it("accepts valid data", () => {
    const r = createWarehouseSchema.safeParse({ code: "bue", name: "Depósito Central", countryId: UUID })
    expect(r.success).toBe(true)
    if (r.success) expect(r.data.code).toBe("BUE") // toUpperCase()
  })

  it("rejects empty code", () => {
    const r = createWarehouseSchema.safeParse({ code: "", name: "Test", countryId: UUID })
    expect(r.success).toBe(false)
  })

  it("rejects code longer than 10", () => {
    const r = createWarehouseSchema.safeParse({ code: "TOOLONGCODE", name: "Test", countryId: UUID })
    expect(r.success).toBe(false)
  })
})`} />

            <h3 className="text-sm font-medium mb-2 mt-4">API smoke test sin DB</h3>
            <CodeBlock code={`import { describe, it, expect } from "bun:test"
import { Hono } from "hono"

const API_KEY = "test-key"
process.env.API_KEY = API_KEY

const app = new Hono()
  .get("/health", (c) => c.json({ status: "ok" }))
  .get("/auth-check", (c) => {
    if (c.req.header("X-API-Key") !== API_KEY) return c.json({ error: "Forbidden" }, 403)
    return c.json({ ok: true })
  })

describe("GET /health", () => {
  it("returns 200", async () => {
    const res = await app.request("/health")
    expect(res.status).toBe(200)
  })
})`} />
            <p className="text-sm text-muted-foreground mt-3">
              Correr con <code className="text-xs font-mono">bun test</code> dentro del paquete o <code className="text-xs font-mono">bun run test</code> desde la raíz.
            </p>
          </section>

          {/* ─── 6. Checklist ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">6 · Checklist de implementación</h2>
            <ul className="list-disc list-inside text-sm text-muted-foreground space-y-1.5">
              <li><code className="text-xs font-mono">schema.ts</code>: tabla con <code className="text-xs font-mono">deletedAt</code> nullable, <code className="text-xs font-mono">code</code> NOT NULL si aplica.</li>
              <li><code className="text-xs font-mono">db:push</code> (dev) o <code className="text-xs font-mono">db:generate</code> + <code className="text-xs font-mono">db:migrate</code> (prod). <strong>Ojo:</strong> <code className="text-xs font-mono">db:generate</code> solo crea el archivo SQL — sin <code className="text-xs font-mono">db:migrate</code> la tabla no existe en la DB y las queries fallan en runtime.</li>
              <li>Schema Zod en <code className="text-xs font-mono">packages/shared/src/schemas/</code> con <code className="text-xs font-mono">code: .min(1)</code> y exportado en <code className="text-xs font-mono">index.ts</code>.</li>
              <li>Route API <strong>encadenada</strong> desde <code className="text-xs font-mono">new Hono()</code>: GET con <code className="text-xs font-mono">?deleted</code>, POST, PATCH, DELETE (soft), PATCH restore.</li>
              <li>Route registrada en <code className="text-xs font-mono">routes/index.ts</code>.</li>
              <li>PATCH en <code className="text-xs font-mono">allowMethods</code> de CORS en <code className="text-xs font-mono">src/index.ts</code> (ya incluido desde v0.7.0).</li>
              <li>Frontend: <code className="text-xs font-mono">filterStatus</code> state, <code className="text-xs font-mono">authorizedFetch</code> en <code className="text-xs font-mono">queryFn</code> (no <code className="text-xs font-mono">fetch + getHeaders</code> directo), queryKey incluye <code className="text-xs font-mono">filterStatus</code>, <code className="text-xs font-mono">restoreMut</code> con <code className="text-xs font-mono">onError</code> + <code className="text-xs font-mono">restoringId</code> por fila, botón edit oculto cuando <code className="text-xs font-mono">row.deletedAt</code> tiene valor.</li>
              <li><strong>Sidebar</strong>: agregar la entrada al array <code className="text-xs font-mono">navXxx</code> correspondiente en <code className="text-xs font-mono">apps/web/src/components/app-sidebar.tsx</code>. Sin esto la página existe pero es inalcanzable desde la UI.</li>
              <li><code className="text-xs font-mono">PATCH /:id</code> (update): verificar <code className="text-xs font-mono">deletedAt</code> y retornar 409 si el registro está soft-deleted.</li>
              <li>Endpoint <code className="text-xs font-mono">/count</code>: agregar <code className="text-xs font-mono">.where(isNull(tabla.deletedAt))</code> para contar solo registros activos.</li>
              <li>Tests: al menos schema Zod + smoke test de API.</li>
            </ul>
          </section>

          {/* ─── 7. Errores típicos ─── */}
          <section>
            <h2 className="text-base font-semibold mb-3">7 · Errores típicos al agregar una entidad nueva</h2>
            <p className="text-sm text-muted-foreground mb-4">
              Estos son los errores más frecuentes al copiar un módulo existente para crear uno nuevo. Todos han ocurrido en el proyecto.
            </p>

            <div className="space-y-4">

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Olvidar <code className="text-xs font-mono">db:migrate</code> tras <code className="text-xs font-mono">db:generate</code></h3>
                <p className="text-sm text-muted-foreground">
                  <code className="text-xs font-mono">db:generate</code> solo genera el archivo <code className="text-xs font-mono">.sql</code> en <code className="text-xs font-mono">migrations/</code>.
                  La tabla no existe en PostgreSQL hasta correr <code className="text-xs font-mono">db:migrate</code>.
                  El síntoma es un 500 o error <em>"relation does not exist"</em> al hacer cualquier request a la nueva entidad.
                </p>
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-2">
                  ✅ Fix: <code className="text-xs font-mono">cd apps/api && bun run db:migrate</code>
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ <code className="text-xs font-mono">db:migrate</code> saltea la migration porque el nombre ya está en <code className="text-xs font-mono">__drizzle_migrations</code></h3>
                <p className="text-sm text-muted-foreground">
                  Ocurre cuando en dev se mezclan <code className="text-xs font-mono">db:push</code> con <code className="text-xs font-mono">db:generate/migrate</code>.
                  Si se usó <code className="text-xs font-mono">db:push</code> para sincronizar la DB y después se corrió <code className="text-xs font-mono">db:generate</code>,
                  Drizzle puede regenerar una migration existente en lugar de crear una nueva.
                  Al correr <code className="text-xs font-mono">db:migrate</code>, ve ese nombre (ej. <code className="text-xs font-mono">0001_perfect_scarecrow</code>) como ya
                  aplicado y lo ignora silenciosamente. El output solo muestra los NOTICE de PostgreSQL, sin error, pero la tabla nunca se crea.
                </p>
                <p className="text-sm text-muted-foreground mt-2">
                  Síntoma: <code className="text-xs font-mono">db:migrate</code> termina sin errores pero la tabla sigue sin existir en la DB.
                </p>
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-2">
                  ✅ Fix en dev: <code className="text-xs font-mono">bun run db:push</code> — diffea el schema actual contra la DB real y aplica solo lo que falta, sin tocar datos existentes. Evitá mezclar <code className="text-xs font-mono">db:push</code> y <code className="text-xs font-mono">db:migrate</code> en el mismo entorno.
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Olvidar agregar la ruta al sidebar</h3>
                <p className="text-sm text-muted-foreground">
                  La ruta existe en <code className="text-xs font-mono">App.tsx</code> y la página funciona si se navega directamente por URL,
                  pero no aparece en el menú lateral porque el array <code className="text-xs font-mono">navXxx</code> en
                  <code className="text-xs font-mono"> app-sidebar.tsx</code> nunca fue actualizado.
                </p>
                <p className="text-sm text-amber-700 bg-amber-50 border border-amber-200 rounded px-3 py-1.5 mt-2">
                  ✅ Fix: agregar la entrada en el array correspondiente de <code className="text-xs font-mono">app-sidebar.tsx</code>.
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Copy-paste del <code className="text-xs font-mono">queryKey</code> y/o <code className="text-xs font-mono">queryFn</code> del módulo origen</h3>
                <p className="text-sm text-muted-foreground">
                  Al copiar una página existente, es fácil olvidar cambiar el <code className="text-xs font-mono">queryKey</code> y la llamada dentro de
                  <code className="text-xs font-mono"> queryFn</code>. El resultado: la nueva página llama al endpoint del módulo origen
                  (p.ej. <code className="text-xs font-mono">api.trips["service-types"].$get</code> en vez de <code className="text-xs font-mono">api.trips["trip-types"].$get</code>),
                  muestra datos incorrectos silenciosamente, y las invalidaciones no coinciden con la query real.
                </p>
                <CodeBlock code={`// ❌ Mal — queryKey y endpoint del módulo copiado
const { data } = useQuery({
  queryKey: ["trips", "service-types", filterStatus],
  queryFn: () => api.trips["service-types"].$get({ query }),
})

// ✅ Bien — todo consistente con la entidad nueva
const { data } = useQuery({
  queryKey: ["trips", "trip-types", filterStatus],
  queryFn: () => api.trips["trip-types"].$get({ query }),
})`} />
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Botón editar visible en registros soft-deleted</h3>
                <p className="text-sm text-muted-foreground">
                  Cuando <code className="text-xs font-mono">row.deletedAt</code> tiene valor, el botón de editar debe ocultarse y mostrar solo
                  el botón de restaurar. El patrón incorrecto muestra ambos.
                </p>
                <CodeBlock code={`// ❌ Mal — editar siempre visible
<Button onClick={() => openEdit(item)}><PencilIcon /></Button>
{item.deletedAt ? <RestoreIcon /> : <Trash2Icon />}

// ✅ Bien — editar oculto cuando está eliminado
{item.deletedAt ? (
  <Button onClick={() => restoreMut.mutate(item.id)}><RestoreIcon /></Button>
) : (
  <>
    <Button onClick={() => openEdit(item)}><PencilIcon /></Button>
    <Button onClick={() => openDelete(item)}><Trash2Icon /></Button>
  </>
)}`} />
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ <code className="text-xs font-mono">restoreMut</code> sin <code className="text-xs font-mono">onError</code></h3>
                <p className="text-sm text-muted-foreground">
                  <code className="text-xs font-mono">authorizedFetch</code> lanza en errores HTTP. Sin <code className="text-xs font-mono">onError</code> en <code className="text-xs font-mono">useMutation</code>,
                  los errores se pierden silenciosamente y el usuario no recibe feedback.
                </p>
                <CodeBlock code={`// ❌ Mal
const restoreMut = useMutation({
  mutationFn: async (id: string) => { await authorizedFetch(...) },
  onSuccess: () => { qc.invalidateQueries(...) },
  // sin onError
})

// ✅ Bien
const restoreMut = useMutation({
  mutationFn: async (id: string) => { await authorizedFetch(...) },
  onSuccess: () => { qc.invalidateQueries(...) },
  onError: (err: Error) => setFormError(err.message),
})`} />
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ PATCH update sin guard de soft-delete</h3>
                <p className="text-sm text-muted-foreground">
                  El endpoint <code className="text-xs font-mono">PATCH /:id</code> (update) debe comprobar <code className="text-xs font-mono">target.deletedAt</code>
                  y retornar 409 si el registro está eliminado. Sin esta guarda, es posible editar un registro borrado desde herramientas externas (Postman, curl).
                </p>
                <CodeBlock code={`// ❌ Mal — no verifica deletedAt
const [target] = await db.select({ id: t.id }).from(t).where(eq(t.id, id)).limit(1)
if (!target) return c.json({ error: "No encontrado" }, 404)

// ✅ Bien
const [target] = await db.select({ id: t.id, deletedAt: t.deletedAt }).from(t)
  .where(eq(t.id, id)).limit(1)
if (!target) return c.json({ error: "No encontrado" }, 404)
if (target.deletedAt) return c.json({ error: "Registro eliminado, restauralo primero" }, 409)`} />
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Endpoint <code className="text-xs font-mono">/count</code> sin filtro de activos</h3>
                <p className="text-sm text-muted-foreground">
                  Si el endpoint <code className="text-xs font-mono">/count</code> no filtra <code className="text-xs font-mono">WHERE deleted_at IS NULL</code>,
                  el conteo incluye registros soft-deleted y el dashboard muestra cifras incorrectas.
                </p>
                <CodeBlock code={`// ❌ Mal — cuenta todos, incluidos borrados
await db.select({ count: sql\`cast(count(*) as integer)\` }).from(tripTypes)

// ✅ Bien
await db.select({ count: sql\`cast(count(*) as integer)\` })
  .from(tripTypes)
  .where(isNull(tripTypes.deletedAt))`} />
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Nombre de tipo TypeScript que colisiona con el nombre del schema Zod</h3>
                <p className="text-sm text-muted-foreground">
                  En los schemas de <code className="text-xs font-mono">shared</code>, si el <code className="text-xs font-mono">export type</code> tiene el mismo nombre
                  (y mismo casing) que el schema Zod, TypeScript se confunde. Seguir la convención: schemas en
                  <code className="text-xs font-mono"> camelCase</code>, tipos en <code className="text-xs font-mono">PascalCase</code>.
                </p>
                <CodeBlock code={`// ❌ Mal — el tipo y el schema tienen el mismo identificador
export const tripTypeIdSchema = z.string().uuid()
export type tripTypeIdSchema = z.infer<typeof tripTypeIdSchema>  // ← collision!

// ✅ Bien
export const tripTypeIdSchema = z.string().uuid()
export type TripTypeId = z.infer<typeof tripTypeIdSchema>`} />
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Comentarios de sección copiados y no actualizados</h3>
                <p className="text-sm text-muted-foreground">
                  Al copiar bloques de código, los comentarios <code className="text-xs font-mono">// ─── NombreA</code> se quedan apuntando
                  al módulo origen. No rompe nada en runtime, pero genera confusión al leer el código meses después.
                  Actualizar siempre los comentarios de sección al renombrar.
                </p>
              </div>

              <div className="border rounded-lg p-4">
                <h3 className="text-sm font-semibold text-destructive mb-1">❌ Log message del módulo origen en el nuevo módulo</h3>
                <p className="text-sm text-muted-foreground">
                  Copiar un handler y olvidar cambiar el mensaje de <code className="text-xs font-mono">logger.info/error</code> genera
                  logs engañosos (p.ej. <em>"Service type soft deleted"</em> aparece en los logs cuando en realidad se está borrando un trip type).
                  Siempre actualizar el string del log al copiar handlers.
                </p>
              </div>

            </div>
          </section>

        </div>
      </div>
    </>
  )
}
